import React, { useState, useEffect } from "react";
import {
  Box, Button, TextField, Typography, CircularProgress,
  InputAdornment, IconButton, Alert,
} from "@mui/material";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import EmailIcon from "@mui/icons-material/Email";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import RestaurantIcon from "@mui/icons-material/Restaurant";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import { useNavigate } from "react-router-dom";

const API_URL = (process.env.REACT_APP_API_URL || "").trim();

type Mode = "login" | "forgot" | "reset" | "reset_done";

const inputSx = { "& .MuiOutlinedInput-root": { borderRadius: 2, bgcolor: "#f8f9ff" } };

const PortalLogin: React.FC = () => {
  const navigate = useNavigate();
  const [mode, setMode] = useState<Mode>("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [newPw, setNewPw] = useState("");
  const [confirmPw, setConfirmPw] = useState("");
  const [resetToken, setResetToken] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  useEffect(() => {
    const token = localStorage.getItem("portal_token");
    const shopId = localStorage.getItem("portal_shop_id");
    if (token && shopId) navigate(`/restaurant-portal/${shopId}/dashboard`, { replace: true });
    const params = new URLSearchParams(window.location.search);
    const t = params.get("token");
    if (t) { setResetToken(t); setMode("reset"); }
  }, [navigate]);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      const res = await fetch(`${API_URL}/restaurant-portal/login`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password }),
      });
      const data = await res.json();
      if (!data.success) throw new Error(data.message || "Login failed");
      localStorage.setItem("portal_token", data.token);
      localStorage.setItem("portal_shop_id", String(data.shop_id));
      navigate(`/restaurant-portal/${data.shop_id}/dashboard`, { replace: true });
    } catch (err: any) { setError(err.message); } finally { setLoading(false); }
  };

  const handleForgot = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      const res = await fetch(`${API_URL}/restaurant-portal/forgot-password`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      });
      const data = await res.json();
      if (!data.success) throw new Error(data.message);
      setSuccess("A reset link has been generated. Please contact your supplier to receive it.");
    } catch (err: any) { setError(err.message); } finally { setLoading(false); }
  };

  const handleReset = async (e: React.FormEvent) => {
    e.preventDefault();
    if (newPw !== confirmPw) { setError("Passwords do not match"); return; }
    setError(""); setLoading(true);
    try {
      const res = await fetch(`${API_URL}/restaurant-portal/reset-password`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token: resetToken, password: newPw, password_confirmation: confirmPw }),
      });
      const data = await res.json();
      if (!data.success) throw new Error(data.message || "Reset failed");
      setMode("reset_done");
    } catch (err: any) { setError(err.message); } finally { setLoading(false); }
  };

  return (
    <Box sx={{ position: "fixed", inset: 0, zIndex: 1200, display: "flex", minHeight: "100vh" }}>
      {/* Left panel - branding */}
      <Box sx={{
        display: { xs: "none", md: "flex" }, width: "45%", bgcolor: "#00838f",
        flexDirection: "column", justifyContent: "center", alignItems: "center", p: 6,
        background: "linear-gradient(160deg, #004d54 0%, #00696e 40%, #00838f 100%)",
      }}>
        <Box sx={{ width: 80, height: 80, borderRadius: 3, bgcolor: "rgba(255,255,255,0.1)", display: "flex", alignItems: "center", justifyContent: "center", mb: 4 }}>
          <RestaurantIcon sx={{ color: "#fff", fontSize: 40 }} />
        </Box>
        <Typography sx={{ color: "#fff", fontSize: 32, fontWeight: 800, mb: 1.5, textAlign: "center", lineHeight: 1.2 }}>
          Restaurant Portal
        </Typography>
        <Typography sx={{ color: "rgba(255,255,255,0.6)", fontSize: 15, textAlign: "center", maxWidth: 320, lineHeight: 1.6 }}>
          Manage your orders, browse products, and track deliveries — all in one place.
        </Typography>
        <Box sx={{ mt: 6, display: "flex", gap: 3 }}>
          {["Place Orders", "Track Delivery", "Manage Products"].map(t => (
            <Box key={t} sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
              <CheckCircleOutlineIcon sx={{ fontSize: 16, color: "rgba(255,255,255,0.5)" }} />
              <Typography sx={{ fontSize: 12, color: "rgba(255,255,255,0.5)", fontWeight: 500 }}>{t}</Typography>
            </Box>
          ))}
        </Box>
      </Box>

      {/* Right panel - form */}
      <Box sx={{
        flex: 1, display: "flex", alignItems: "center", justifyContent: "center",
        bgcolor: "#fff", p: { xs: 3, sm: 5 },
      }}>
        <Box sx={{ width: "100%", maxWidth: 400 }}>
          {/* Mobile logo */}
          <Box sx={{ display: { xs: "flex", md: "none" }, alignItems: "center", gap: 1.5, mb: 4, justifyContent: "center" }}>
            <Box sx={{ width: 44, height: 44, borderRadius: 2, bgcolor: "#00838f", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <RestaurantIcon sx={{ color: "#fff", fontSize: 22 }} />
            </Box>
            <Typography fontWeight={800} fontSize={20} color="#00838f">Restaurant Portal</Typography>
          </Box>

          <Typography sx={{ fontSize: 26, fontWeight: 800, color: "#00838f", mb: 0.5 }}>
            {mode === "login" ? "Welcome back" : mode === "forgot" ? "Forgot Password" : mode === "reset" ? "Reset Password" : "All done!"}
          </Typography>
          <Typography sx={{ fontSize: 14, color: "#999", mb: 3.5 }}>
            {mode === "login" ? "Sign in to your restaurant account" : mode === "forgot" ? "Enter your email to receive a reset link" : mode === "reset" ? "Create your new password" : "Your password has been updated"}
          </Typography>

          {error && <Alert severity="error" sx={{ mb: 2.5, borderRadius: 2, fontSize: 13 }}>{error}</Alert>}
          {success && <Alert severity="success" sx={{ mb: 2.5, borderRadius: 2, fontSize: 13 }}>{success}</Alert>}

          {mode === "login" && (
            <form onSubmit={handleLogin}>
              <Typography fontSize={12} fontWeight={600} color="#666" mb={0.75}>Email Address</Typography>
              <TextField fullWidth size="small" placeholder="Enter your email" type="email"
                value={email} onChange={(e) => setEmail(e.target.value)} required
                InputProps={{ startAdornment: <InputAdornment position="start"><EmailIcon sx={{ fontSize: 18, color: "#bbb" }} /></InputAdornment> }}
                sx={{ mb: 2.5, ...inputSx }}
              />
              <Typography fontSize={12} fontWeight={600} color="#666" mb={0.75}>Password</Typography>
              <TextField fullWidth size="small" placeholder="Enter your password"
                type={showPw ? "text" : "password"} value={password} onChange={(e) => setPassword(e.target.value)} required
                InputProps={{
                  startAdornment: <InputAdornment position="start"><LockOutlinedIcon sx={{ fontSize: 18, color: "#bbb" }} /></InputAdornment>,
                  endAdornment: <InputAdornment position="end"><IconButton size="small" onClick={() => setShowPw(v => !v)}>{showPw ? <VisibilityOffIcon sx={{ fontSize: 18 }} /> : <VisibilityIcon sx={{ fontSize: 18 }} />}</IconButton></InputAdornment>,
                }}
                sx={{ mb: 1.5, ...inputSx }}
              />
              <Box sx={{ textAlign: "right", mb: 3 }}>
                <Typography component="span" fontSize={13} color="#00838f" fontWeight={600}
                  sx={{ cursor: "pointer", "&:hover": { textDecoration: "underline" } }}
                  onClick={() => { setMode("forgot"); setError(""); setSuccess(""); }}>
                  Forgot password?
                </Typography>
              </Box>
              <Button fullWidth type="submit" variant="contained" disabled={loading}
                sx={{ borderRadius: 2, py: 1.5, fontWeight: 700, fontSize: 15, bgcolor: "#00838f", "&:hover": { bgcolor: "#006064" }, textTransform: "none", boxShadow: "0 4px 14px rgba(0,150,136,0.35)" }}>
                {loading ? <CircularProgress size={22} sx={{ color: "#fff" }} /> : "Sign In"}
              </Button>
            </form>
          )}

          {mode === "forgot" && !success && (
            <form onSubmit={handleForgot}>
              <Typography fontSize={12} fontWeight={600} color="#666" mb={0.75}>Email Address</Typography>
              <TextField fullWidth size="small" placeholder="Enter your email" type="email"
                value={email} onChange={(e) => setEmail(e.target.value)} required
                InputProps={{ startAdornment: <InputAdornment position="start"><EmailIcon sx={{ fontSize: 18, color: "#bbb" }} /></InputAdornment> }}
                sx={{ mb: 3, ...inputSx }}
              />
              <Button fullWidth type="submit" variant="contained" disabled={loading}
                sx={{ borderRadius: 2, py: 1.5, fontWeight: 700, bgcolor: "#00838f", textTransform: "none" }}>
                {loading ? <CircularProgress size={20} sx={{ color: "#fff" }} /> : "Request Reset Link"}
              </Button>
              <Button fullWidth variant="text" startIcon={<ArrowBackIcon />}
                onClick={() => { setMode("login"); setError(""); setSuccess(""); }}
                sx={{ mt: 1.5, borderRadius: 2, color: "#999", textTransform: "none" }}>
                Back to login
              </Button>
            </form>
          )}

          {mode === "forgot" && success && (
            <Button fullWidth variant="outlined" startIcon={<ArrowBackIcon />}
              onClick={() => { setMode("login"); setError(""); setSuccess(""); }}
              sx={{ borderRadius: 2, color: "#00838f", borderColor: "#a5b4fc", textTransform: "none" }}>
              Back to login
            </Button>
          )}

          {mode === "reset" && (
            <form onSubmit={handleReset}>
              <Typography fontSize={12} fontWeight={600} color="#666" mb={0.75}>New Password</Typography>
              <TextField fullWidth size="small" type="password" placeholder="Min. 6 characters"
                value={newPw} onChange={(e) => setNewPw(e.target.value)} required inputProps={{ minLength: 6 }}
                sx={{ mb: 2.5, ...inputSx }} />
              <Typography fontSize={12} fontWeight={600} color="#666" mb={0.75}>Confirm Password</Typography>
              <TextField fullWidth size="small" type="password" placeholder="Re-enter password"
                value={confirmPw} onChange={(e) => setConfirmPw(e.target.value)} required
                sx={{ mb: 3, ...inputSx }} />
              <Button fullWidth type="submit" variant="contained" disabled={loading}
                sx={{ borderRadius: 2, py: 1.5, fontWeight: 700, bgcolor: "#00838f", textTransform: "none" }}>
                {loading ? <CircularProgress size={20} sx={{ color: "#fff" }} /> : "Set New Password"}
              </Button>
            </form>
          )}

          {mode === "reset_done" && (
            <Box textAlign="center">
              <CheckCircleOutlineIcon sx={{ fontSize: 56, color: "#2e7d32", mb: 2 }} />
              <Typography fontSize={15} fontWeight={600} color="#2e7d32" mb={3}>Password updated successfully!</Typography>
              <Button fullWidth variant="contained" onClick={() => { setMode("login"); setError(""); setSuccess(""); }}
                sx={{ borderRadius: 2, py: 1.5, fontWeight: 700, bgcolor: "#00838f", textTransform: "none" }}>
                Sign In Now
              </Button>
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default PortalLogin;
